
import React, { useState, useEffect } from 'react';
import { useSales } from './hooks/useSales';
import { useProducts } from '../products/hooks/useProducts';
import { useCustomer } from '../customer/hooks/useCustomer';

interface SalesOrderBookingProps {
  sessionId?: string;
  customerId?: string;
}

export const SalesOrderBooking: React.FC<SalesOrderBookingProps> = ({ sessionId, customerId }) => {
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [quantity, setQuantity] = useState(1);
  const [paymentPercentage, setPaymentPercentage] = useState(15);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [goldRateFrozen, setGoldRateFrozen] = useState(false);
  
  const { products, goldRate } = useProducts();
  const { customers } = useCustomer();
  const { createOrder, calculatePrice } = useSales();

  const activeProducts = products?.filter(p => p.status === 'active' && p.margin_fixed) || [];

  const handleProductSelect = (product: any) => {
    setSelectedProduct(product);
  };

  const calculateTotalAmount = () => {
    if (!selectedProduct || !goldRate) return 0;
    
    const basePrice = calculatePrice(selectedProduct, goldRate.rate_24k, quantity);
    return basePrice;
  };

  const handleOrderBooking = async () => {
    if (!selectedProduct || !customerId) return;

    const orderData = {
      customer_id: customerId,
      product_id: selectedProduct.id,
      vendor_id: selectedProduct.vendor_id,
      quantity,
      gold_rate_frozen: goldRateFrozen ? goldRate.rate_24k : null,
      total_amount: calculateTotalAmount(),
      advance_paid: (calculateTotalAmount() * paymentPercentage) / 100,
      payment_percentage: paymentPercentage,
      special_instructions: specialInstructions,
      status: 'booked'
    };

    try {
      await createOrder.mutateAsync(orderData);
      alert('Order booked successfully!');
      setSelectedProduct(null);
      setQuantity(1);
      setSpecialInstructions('');
    } catch (error) {
      console.error('Failed to book order:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-primary">Sales Order Booking</h1>
        <div className="text-sm text-gray-600">
          Gold Rate (24K): ₹{goldRate?.rate_24k || 0}/gram
        </div>
      </div>

      {/* Product Catalog */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeProducts.map((product) => (
          <div
            key={product.id}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedProduct?.id === product.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleProductSelect(product)}
          >
            <div className="space-y-3">
              {product.images && product.images[0] && (
                <img
                  src={product.images[0]}
                  alt={product.model_name}
                  className="w-full h-48 object-cover rounded"
                />
              )}
              <div>
                <h3 className="font-semibold">{product.model_name}</h3>
                <p className="text-sm text-gray-600">{product.description}</p>
              </div>
              <div className="space-y-1 text-sm">
                <div>Metal: {product.metal_type}</div>
                <div>Weight: {product.gross_weight}g</div>
                <div>Delivery: {product.delivery_days} days</div>
                <div>Making Charges: ₹{product.making_charges}</div>
                {product.customization_possible && (
                  <div className="text-green-600">Customization Available</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Order Details */}
      {selectedProduct && (
        <div className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">Order Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Quantity</label>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Advance Payment %</label>
              <select
                value={paymentPercentage}
                onChange={(e) => setPaymentPercentage(parseInt(e.target.value))}
                className="w-full border rounded px-3 py-2"
              >
                <option value={15}>15% (Standard)</option>
                <option value={85}>85% (Freeze Gold Rate)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={goldRateFrozen}
                onChange={(e) => setGoldRateFrozen(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Freeze current gold rate (requires 85% payment)</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Special Instructions</label>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              className="w-full border rounded px-3 py-2 h-20"
              placeholder="Any customization or special requirements..."
            />
          </div>

          <div className="border-t pt-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Total Amount:</span>
                <span className="font-semibold">₹{calculateTotalAmount().toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Advance Required:</span>
                <span className="font-semibold text-primary">
                  ₹{((calculateTotalAmount() * paymentPercentage) / 100).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleOrderBooking}
            className="w-full bg-primary text-white py-2 rounded hover:bg-primary/90 transition-colors"
          >
            Book Order
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesOrderBooking;
